/**
 * alljourneys-api — the persistence seam for the Appointment Page Designer
 * (rail "All Journeys", route `alljourneys`).
 *
 * Grain (operator, 2026-09-03): ONE RECORD PER JOURNEY PER TENANT — five records
 * (appointment · queue · events · landing · kiosk), each with its own draft /
 * published state and public slug. This is a NEW record type: it does not touch
 * /v1/admin/journeys or the New Journeys documents, by ruling.
 *
 * There is NO contract for this resource yet. The request is filed as
 * `CD-API-REQUEST-page-designer-2026-09-03.md` (root): five ops under
 * /v1/admin/page-designer — list, get, put, publish, unpublish. Every call a
 * screen makes goes through THIS module, so when the contract lands the only
 * change is the body of each function below.
 *
 * Model conformance (Data Model Fit review, Part 0):
 *  • No `active` boolean. A record is live when `published_at` is set and
 *    `active_to` is null — decision 63 (activation vs status).
 *  • `slug` is the record's external key on the public router (decision on
 *    external keys) and is registered on publish (operator: "also register the
 *    public slug so /book/{slug} resolves").
 *  • Every write stamps `updated_at`; publish snapshots the settings that were
 *    live (`published_s`, `published_ct`) so a later draft edit does not change
 *    what a visitor sees — resolve-at-write (decision 58).
 *
 * Until the contract lands each op resolves from a per-tenant localStorage
 * envelope (`alljourneys.store.<slug>.v1`) shaped exactly like the requested
 * response, and every resolved value is marked `__source: "cd-local-stub"`.
 */

export type PdJourneyId = "appointment" | "queue" | "events" | "landing" | "kiosk";
export const PD_JOURNEYS: PdJourneyId[] = ["appointment", "queue", "events", "landing", "kiosk"];

export interface PdRecord {
  journey: PdJourneyId;
  /** Draft settings — what the designer edits. */
  s: Record<string, unknown>;
  /** Draft custom text overrides. */
  ct: Record<string, string>;
  /** Public slug — the record's external key on the public router. */
  slug: string;
  /** ISO timestamps. `published_at` null = never published. `active_to` set = retired. */
  created_at: string;
  updated_at: string;
  published_at: string | null;
  active_to: string | null;
  /** Snapshot of what is LIVE, taken at publish. Null until first publish. */
  published_s: Record<string, unknown> | null;
  published_ct: Record<string, string> | null;
  __source?: string;
}

export interface PdEnvelope {
  records: Partial<Record<PdJourneyId, PdRecord>>;
  /** slug -> journey. The public router resolves /book/{slug} through this. */
  slugs: Record<string, PdJourneyId>;
  __source?: string;
}

const KEY = (tenant: string) => `alljourneys.store.${tenant || "default"}.v1`;

function read(tenant: string): PdEnvelope {
  try {
    const raw = localStorage.getItem(KEY(tenant));
    if (!raw) return { records: {}, slugs: {}, __source: "cd-local-stub" };
    const p = JSON.parse(raw) as Partial<PdEnvelope>;
    return { records: p.records ?? {}, slugs: p.slugs ?? {}, __source: "cd-local-stub" };
  } catch {
    return { records: {}, slugs: {}, __source: "cd-local-stub" };
  }
}
function write(tenant: string, env: PdEnvelope): void {
  try { localStorage.setItem(KEY(tenant), JSON.stringify({ records: env.records, slugs: env.slugs })); }
  catch { /* quota — the in-memory copy the screen holds stays authoritative for the session */ }
}
const now = () => new Date().toISOString();

/** GET /v1/admin/page-designer — all five records + the slug registry for the tenant. */
export async function listPageDesigner(tenant: string): Promise<PdEnvelope> {
  return read(tenant);
}

/** GET /v1/admin/page-designer/{journey} */
export async function getPageDesigner(tenant: string, journey: PdJourneyId): Promise<PdRecord | null> {
  return read(tenant).records[journey] ?? null;
}

/** PUT /v1/admin/page-designer/{journey} — saves the DRAFT. Never touches the published snapshot. */
export async function putPageDesigner(
  tenant: string, journey: PdJourneyId, draft: { s: Record<string, unknown>; ct: Record<string, string>; slug: string },
): Promise<PdRecord> {
  const env = read(tenant);
  const prev = env.records[journey];
  const rec: PdRecord = {
    journey,
    s: draft.s, ct: draft.ct, slug: draft.slug,
    created_at: prev?.created_at ?? now(),
    updated_at: now(),
    published_at: prev?.published_at ?? null,
    active_to: prev?.active_to ?? null,
    published_s: prev?.published_s ?? null,
    published_ct: prev?.published_ct ?? null,
    __source: "cd-local-stub",
  };
  env.records[journey] = rec;
  write(tenant, env);
  return rec;
}

/** Slug taken by ANOTHER journey in this tenant — the one collision publish must refuse. */
export function slugConflict(tenant: string, journey: PdJourneyId, slug: string): PdJourneyId | null {
  const owner = read(tenant).slugs[slug];
  return owner && owner !== journey ? owner : null;
}

/** POST /v1/admin/page-designer/{journey}/publish — snapshots the draft as live and
 *  registers the slug so /book/{slug} resolves. Refuses a slug another journey holds. */
export async function publishPageDesigner(tenant: string, journey: PdJourneyId): Promise<PdRecord> {
  const env = read(tenant);
  const rec = env.records[journey];
  if (!rec) throw new Error("Nothing saved for this journey yet.");
  const clash = slugConflict(tenant, journey, rec.slug);
  if (clash) throw new Error(`The link "${rec.slug}" is already used by the ${clash} journey.`);
  // release any slug this journey held before, then register the current one
  for (const k of Object.keys(env.slugs)) if (env.slugs[k] === journey) delete env.slugs[k];
  env.slugs[rec.slug] = journey;
  rec.published_at = now();
  rec.active_to = null;
  rec.published_s = { ...rec.s };
  rec.published_ct = { ...rec.ct };
  rec.updated_at = now();
  write(tenant, env);
  return rec;
}

/** POST /v1/admin/page-designer/{journey}/unpublish — retires the live page (dated,
 *  never deleted) and releases the slug. */
export async function unpublishPageDesigner(tenant: string, journey: PdJourneyId): Promise<PdRecord> {
  const env = read(tenant);
  const rec = env.records[journey];
  if (!rec) throw new Error("Nothing saved for this journey yet.");
  for (const k of Object.keys(env.slugs)) if (env.slugs[k] === journey) delete env.slugs[k];
  rec.active_to = now();
  rec.updated_at = now();
  write(tenant, env);
  return rec;
}

/** What the public router asks: which journey does this slug open, if any is live. */
export function resolvePublicSlug(tenant: string, slug: string): PdJourneyId | null {
  const env = read(tenant);
  const j = env.slugs[slug];
  if (!j) return null;
  const rec = env.records[j];
  return rec && rec.published_at && !rec.active_to ? j : null;
}

/** True while the ops above are stubbed — the screen surfaces it as a provenance note. */
export const PD_STUBBED = true;
